import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV = [
  { anchor: "que-es-eos", label: "¿Qué es EOS?" },
  { anchor: "eos-en-chile", label: "El modelo" },
  { to: "/implementacion", label: "El proceso" },
  { to: "/rompiendo-el-techo", label: "Rompiendo el techo" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  const navHref = (anchor) => isHome ? `#${anchor}` : `/#${anchor}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white border-b border-slate-200/80 shadow-[0_4px_24px_-12px_rgba(10,17,40,0.12)]"
          : "bg-white border-b border-slate-100"
      }`}
    >
      <div className="container-eos">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[60px]" : "h-[72px]"}`}>
          {/* Brand: EOS official logo */}
          <a
            href={isHome ? "#top" : "/"}
            data-testid="header-logo"
            className="group flex items-center shrink-0"
            aria-label="EOS · Entrepreneurial Operating System — Carlos Garcín"
          >
            <img
              src="/eos-logo.png"
              alt="EOS — Entrepreneurial Operating System"
              className={`w-auto select-none transition-all duration-300 ${
                scrolled ? "h-9 sm:h-10" : "h-10 sm:h-11 lg:h-12"
              } group-hover:opacity-90`}
              draggable="false"
            />
          </a>

          {/* Right cluster: Nav + CTA */}
          <div className="flex items-center gap-6 xl:gap-8">
            {/* Desktop Nav */}
            <nav className="hidden xl:flex items-center" data-testid="desktop-nav">
              {NAV.map((item) => {
                const cls = "relative whitespace-nowrap px-3.5 xl:px-4 py-2 text-[14px] font-semibold text-navy-700 hover:text-navy-800 transition-colors group";
                const underline = <span className="absolute left-3.5 right-3.5 xl:left-4 xl:right-4 -bottom-0.5 h-[2px] bg-[#F26522] scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100" />;
                if (item.to) {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={cls}
                    >
                      <span>{item.label}</span>{underline}
                    </Link>
                  );
                }
                return (
                  <a
                    key={item.anchor}
                    href={navHref(item.anchor)}
                    data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={cls}
                  >
                    <span>{item.label}</span>{underline}
                  </a>
                );
              })}
            </nav>

            {/* Divider */}
            <span className="hidden xl:inline-block h-8 w-px bg-slate-200" aria-hidden="true" />

            {/* CTA */}
            <Link
              to="/contacto"
              data-testid="header-cta-button"
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap bg-[#F26522] text-white font-semibold text-[14px] px-5 py-3 rounded-md shadow-sm transition-all duration-200 hover:bg-[#D95214] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#F26522] focus:ring-offset-2"
            >
              Agendar una llamada de 15 minutos
              <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </Link>

            {/* Mobile toggle */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              className="xl:hidden p-2.5 rounded-md border border-slate-200 text-navy-800 hover:bg-slate-50 transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {open && (
          <div className="xl:hidden border-t border-slate-200 py-5 animate-fade-in" data-testid="mobile-nav">
            <nav className="flex flex-col gap-0.5">
              {NAV.map((item) =>
                item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-3 py-3 text-base font-semibold text-navy-800 hover:text-[#F26522] hover:bg-slate-50 rounded-md transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.anchor}
                    href={navHref(item.anchor)}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-3 py-3 text-base font-semibold text-navy-800 hover:text-[#F26522] hover:bg-slate-50 rounded-md transition-colors"
                  >
                    {item.label}
                  </a>
                )
              )}
              <Link
                to="/contacto"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-button"
                className="btn-primary mt-4 w-full"
              >
                Agendar una llamada de 15 minutos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
